/* ─── Shared App Bar ─── */

.header {
  --_accent: var(--appbar-accent, var(--nexus-purple));
  --_accent-end: var(--appbar-accent-end, var(--nexus-pink));
  --_accent-rgb: var(--appbar-accent-rgb, 178, 75, 243);

  /* Compete button — always Carousel's brand, never the host's accent. It is
     Carousel's call to action on every page, white-labelled ones included,
     and an org's colours can be anything — a black brand made it vanish on
     the dark bar. */
  --_compete-start: var(--nexus-purple);
  --_compete-end: var(--nexus-pink);

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--nexus-space-lg);
  height: 64px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 400;
  background: var(--nexus-glass-bg);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transform: translate3d(0, 0, 0);
  border-bottom: 1px solid var(--nexus-overlay-light);
}

/* Gradient accent line on bottom edge */
.header::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, var(--_accent), var(--_accent-end), transparent 80%);
  opacity: 0.3;
}

/* ── Left zone: hamburger + brand ── */
.left {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  flex-shrink: 0;
  min-width: 0;
}

/* ── Search trigger — compact pill ── */
.searchTrigger {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  height: 34px;
  padding: 0 var(--nexus-space-md);
  border-radius: var(--nexus-radius-control);
  border: 1px solid var(--nexus-border);
  background: var(--nexus-overlay-subtle);
  color: var(--nexus-text-muted);
  font-size: var(--nexus-text-sm);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition:
    border-color 200ms ease,
    color 200ms ease,
    box-shadow 200ms ease,
    transform 120ms ease;
  min-width: 200px;
  max-width: 360px;
  flex: 1;
  margin: 0 var(--nexus-space-lg);
}

/* inner gradient glow on hover */
.searchTrigger::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    rgba(var(--_accent-rgb), 0.08),
    rgba(var(--_accent-rgb), 0.04),
    transparent
  );
  opacity: 0;
  transition: opacity 200ms ease;
  pointer-events: none;
}

.searchTrigger:hover {
  border-color: rgba(var(--_accent-rgb), 0.5);
  color: var(--nexus-text);
  box-shadow:
    0 0 16px -4px rgba(var(--_accent-rgb), 0.25),
    inset 0 0 12px -6px rgba(var(--_accent-rgb), 0.15);
}

.searchTrigger:hover::before {
  opacity: 1;
}

.searchTrigger:hover .searchKbd {
  border-color: rgba(var(--_accent-rgb), 0.4);
  color: var(--_accent);
}

.searchTrigger:active {
  border-color: var(--_accent);
  transform: scale(0.98);
  box-shadow: 0 0 8px -2px rgba(var(--_accent-rgb), 0.3);
}

.searchText {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.searchKbd {
  font-family: var(--nexus-font-mono);
  font-size: 0.6875rem;
  line-height: 1;
  padding: 2px 6px;
  border-radius: var(--nexus-radius-sm);
  border: 1px solid var(--nexus-border);
  background: var(--nexus-overlay-subtle);
  color: var(--nexus-text-muted);
  transition:
    border-color 200ms ease,
    color 200ms ease;
}

/* ── Floating Compete CTA ── */
.competeBtn {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-xs);
  height: 44px;
  padding: 0 var(--nexus-space-lg);
  border-radius: var(--nexus-radius-sm);
  background: linear-gradient(135deg, var(--_compete-start), var(--_compete-end));
  color: #fff;
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-semibold);
  text-decoration: none;
  letter-spacing: 0.03em;
  position: relative;
  overflow: hidden;
  align-self: center;
  transition:
    box-shadow 200ms ease,
    transform 120ms ease;
  /* --appbar-compete-shadow lets a host swap the glow, e.g. for a light page */
  box-shadow: var(--appbar-compete-shadow, 0 4px 20px -4px color-mix(in srgb, var(--_compete-start) 50%, transparent));
}

.competeBtn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg,
    color-mix(in oklch, var(--_compete-start), white 15%),
    color-mix(in oklch, var(--_compete-end), white 15%));
  opacity: 0;
  transition: opacity 200ms ease;
}

.competeBtn:hover {
  text-decoration: none;
  box-shadow: var(--appbar-compete-shadow-hover, 0 6px 28px -4px color-mix(in srgb, var(--_compete-start) 70%, transparent));
  transform: translateY(-1px);
}

.competeBtn:hover::before {
  opacity: 1;
}

.competeBtn:active {
  transform: scale(0.97);
  box-shadow: 0 2px 12px -2px color-mix(in srgb, var(--_compete-end) 50%, transparent);
}

.competeBtn svg,
.competeBtn span {
  position: relative;
  z-index: 1;
}

/* ── Right zone ── */
.right {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-sm);
  flex-shrink: 0;
}

/* ── Icon button (theme toggle, actions) ── */
.iconBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--nexus-radius-full);
  border: 1px solid transparent;
  background: transparent;
  color: var(--nexus-text-muted);
  cursor: pointer;
  position: relative;
  transition:
    color 200ms ease,
    border-color 200ms ease,
    box-shadow 200ms ease,
    transform 120ms ease;
}

/* radial glow behind icon on hover */
.iconBtn::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  background: radial-gradient(
    circle,
    rgba(var(--_accent-rgb), 0.15) 0%,
    rgba(var(--_accent-rgb), 0.08) 50%,
    transparent 70%
  );
  opacity: 0;
  transition: opacity 200ms ease;
  pointer-events: none;
}

.iconBtn:hover {
  color: var(--_accent);
  border-color: rgba(var(--_accent-rgb), 0.4);
  box-shadow: 0 0 14px -3px rgba(var(--_accent-rgb), 0.35);
}

.iconBtn:hover::before {
  opacity: 1;
}

.iconBtn:active {
  color: var(--nexus-pink);
  border-color: var(--nexus-pink);
  transform: scale(0.88);
  box-shadow: 0 0 8px -2px rgba(255, 45, 138, 0.4);
}

/* ── Actions slot (fullscreen, etc.) ── */
.actions {
  display: flex;
  align-items: center;
}

/* ── Theme toggle (hidden on mobile — moves to the UserMenu) ── */
.themeToggleBtn {
  composes: iconBtn;
}

.fullscreenBtn {
  composes: iconBtn;
}

/* ── Hamburger (mobile only by default) ── */
.menuBtn {
  composes: iconBtn;
  display: none;
}

/* When alwaysShowMenu is set, keep the hamburger visible at all widths. */
.menuBtnVisible {
  display: flex;
}

/* ── Search icon (mobile only) ── */
.searchIconBtn {
  composes: iconBtn;
  display: none;
}

/* ── Mobile ── */
@media (max-width: 768px) {
  .header {
    padding: 0 var(--nexus-space-md);
    height: 56px;
  }

  .left {
    gap: var(--nexus-space-xs);
  }

  .themeToggleBtn,
  .fullscreenBtn {
    display: none;
  }

  .menuBtn {
    display: flex;
  }

  .searchTrigger {
    display: none;
  }

  .searchIconBtn {
    display: flex;
  }

  .competeBtn span {
    display: none;
  }

  .competeBtn {
    padding: 0 var(--nexus-space-sm);
    height: 38px;
    width: 38px;
    justify-content: center;
  }

}
